"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useLocalStorageObject = useLocalStorageObject;
exports.useLocalStorageString = useLocalStorageString;
var _react = require("react");
/**
 * I wasn't smart enough to figure out how to make the string implementation work with the generic type, so it has its
 * own implementation completely independent of the object implementation.
 */

/**
 * Provides a getter and a setter for a localStorage item--the string implementation.
 * @param key The key under which to save value.
 * @param defaultValue The default string value to use if the key is not found. The default value will be saved in local storage.
 * @returns A tuple containing: 1) the value in local storage under that key, or defaultValue if the key is not found, or
 * null if neither the key is found nor defaultValue is given; 2) a setter for updating the value; and 3) a function to
 * remove the value. The value is updated any time local storage changes.
 */

function useLocalStorageString(key, defaultValue) {
  var _ref, _getItemString;
  const [value, setValue] = (0, _react.useState)((_ref = (_getItemString = getItemString(key)) !== null && _getItemString !== void 0 ? _getItemString : defaultValue) !== null && _ref !== void 0 ? _ref : null);

  // Keeps track of whether the state needs to be saved to local storage. If a default value is provided, then it will
  // be initialized with true so that the default value will be saved.
  const refSaveNeeded = (0, _react.useRef)(defaultValue != null);

  // Load the initial state from localStorage
  (0, _react.useEffect)(() => {
    const data = getItemString(key);
    if (data != null) {
      setValue(data);
    }
  }, []);

  // When the caller sets the state, update the state and mark as needing saving.
  const observableSetValue = (0, _react.useCallback)(newState => {
    setValue(newState);
    refSaveNeeded.current = true;
  }, []);
  const observableRemoveValue = (0, _react.useCallback)(() => {
    setValue(null);
    refSaveNeeded.current = true;
  }, []);

  // If state changes, save it to localStorage but only if it needs to be saved (indicating the caller set the state).
  (0, _react.useEffect)(() => {
    if (refSaveNeeded.current) {
      refSaveNeeded.current = false;
      setItemString(value, key);

      // Storage events are only received if a change is made in _another_ window. Dispatch a custom event so that other
      // components in the current document can react to the change. (React's useContext would be more "correct", but
      // this solution is three extra lines.)
      window.dispatchEvent(new StorageEvent('custom_use_local_storage_string_event'));
    }
  }, [value]);

  // If localStorage changes, update the state, but do not mark as needing saving.
  (0, _react.useEffect)(() => {
    const updateStateFromLocalStorage = () => {
      const data = getItemString(key);
      setValue(data);
    };
    window.addEventListener('storage', updateStateFromLocalStorage);
    window.addEventListener('custom_use_local_storage_string_event', updateStateFromLocalStorage);
    return () => {
      window.removeEventListener('storage', updateStateFromLocalStorage);
      window.removeEventListener('custom_use_local_storage_string_event', updateStateFromLocalStorage);
    };
  }, []);
  return [value, observableSetValue, observableRemoveValue];
}
function setItemString(data, key) {
  if (data == null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, data);
  }
}
function getItemString(key) {
  return localStorage.getItem(key);
}

/**
 * Provides a getter and a setter for a localStorage item--the object implementation.
 * @param key The key under which to save value. 
 * @param defaultValue The default value to use if the key is not found. The default value will be serialized using
 * JSON.stringify and saved in local storage.
 * @returns A tuple containing: 1) the value in local storage under that key, or defaultValue if the key is not found, or
 * null if neither the key is found nor defaultValue is given; 2) a setter for updating the value; and 3) a function to
 * remove the value. The value is updated any time local storage changes.
 */

function useLocalStorageObject(key, defaultValue) {
  const [value, setValue, removeValue] = useLocalStorageString(key, JSON.stringify(defaultValue));
  const valueObject = (0, _react.useMemo)(() => {
    return JSON.parse(value);
  }, [value]);
  const setValueObject = (0, _react.useCallback)(newState => setValue(JSON.stringify(newState)), [setValue]);
  return [valueObject, setValueObject, removeValue];
}